import { useApi } from '@backstage/core-plugin-api';
import { notificationApiRef } from '../../../apis/notificationApi';
import { toolsProvisioningApiRef } from '../../../apis/toolsProvisioningApi';
import { useEffect, useState } from 'react';

import { QeScoreSummary } from '../components/qescore/interfaces';
import { mapQeScoreSummaryResponse } from '../components/qescore/utils/mappers';
import { ERROR_TYPE } from '../components/qescore/constants';

function isNoQeMetricsLoadError(errorMessage: string): boolean {
  return errorMessage
    .toLowerCase()
    .includes(ERROR_TYPE.KPI_BREAKDOWN_DATA_FETCH_FAILED);
}

export function useQeScoreSummary(
  apmNumber: string,
  requestId: string,
  onAnalysisDataLoaded?: (rawData: unknown) => void,
) {
  const notificationApi = useApi(notificationApiRef);
  const toolsprovisioningApi = useApi(toolsProvisioningApiRef);

  const [qeScoreSummary, setQeScoreSummary] = useState<QeScoreSummary | null>(
    null,
  );
  const [isQeScoreSummaryLoading, setIsQeScoreSummaryLoading] = useState(false);
  const [isRefreshingAnalysisStatus, setIsRefreshingAnalysisStatus] =
    useState(false);
  const [error, setError] = useState(false);
  const [errorType, setErrorType] = useState<
    'none' | 'no-qe-metrics' | 'generic'
  >('none');

  /** Retrieves QE Score Current month Score, last 6-month trend and 12 KPI metrics breakdown data for given APM number */
  const fetchQeScoreTrendAndKpiData = async () => {
    setIsQeScoreSummaryLoading(true);
    setError(false);
    setErrorType('none');

    if (toolsprovisioningApi) {
      try {
        const response = await toolsprovisioningApi.getQeScoreTrend(
          apmNumber,
          requestId,
        );

        if (response) {
          if (response.analysisData) {
            onAnalysisDataLoaded?.(response.analysisData);
          }
          setQeScoreSummary(mapQeScoreSummaryResponse(response));
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : `${err ?? 'Unknown error'}`;

        setError(true);
        setErrorType(
          isNoQeMetricsLoadError(errorMessage) ? 'no-qe-metrics' : 'generic',
        );
        if (!isNoQeMetricsLoadError(errorMessage)) {
          notificationApi.sendNotification({
            message: `Error occurred - ${errorMessage}`,
            disapperAfterMs: 4000,
            severity: 'error',
          });
        }
      } finally {
        setIsQeScoreSummaryLoading(false);
      }
    }
    return null;
  };

  /**
   * Fetches the QE Score Analysis status for the current date
   * so Analysis can be disabled if DONE
   * or re-enabled if FAILED (allowing retries in case of transient failures)
   */
  const fetchQeScoreAnalysisStatus = async () => {
    if (!toolsprovisioningApi) return;
    setIsRefreshingAnalysisStatus(true);
    try {
      const response = await toolsprovisioningApi.getQeScoreAnalysisStatus(
        apmNumber,
        requestId,
      );
      if (response) {
        const status: string = response?.status;

        if (status === 'DONE' || status === 'FAILED') {
          setQeScoreSummary(prev =>
            prev ? { ...prev, analysisDoneToday: status } : prev,
          );
        }
      }
    } catch {
      // Non-critical
    } finally {
      setIsRefreshingAnalysisStatus(false);
    }
  };

  useEffect(() => {
    fetchQeScoreTrendAndKpiData();
  }, [apmNumber]);

  return {
    qeScoreSummary,
    isQeScoreSummaryLoading,
    isRefreshingAnalysisStatus,
    error,
    errorType,
    fetchQeScoreAnalysisStatus,
  };
}
